import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Shield, Building2, Package, Truck, Users, FileText,
  Clock, TrendingUp, Loader2, CheckCircle, AlertTriangle,
  LogOut, ArrowLeft, BarChart3, Send
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

const CHART_COLORS = ["#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
const LOGO_URL = "https://customer-assets.emergentagent.com/job_0d053155-ce00-4580-8f42-e450395a24b2/artifacts/1forsbvk_logo%20cmes.png";

const formatCurrency = (value) =>
  new Intl.NumberFormat("fr-CI", { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(value || 0);

const AdminDashboard = () => {
  const { token, user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courierData, setCourierData] = useState(null);
  const [activeTab, setActiveTab] = useState("couriers");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/couriers`, { headers: { Authorization: `Bearer ${token}` } });
      setCourierData(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#0f172a" }}>
        <Loader2 className="w-12 h-12 text-sky-500 animate-spin" />
      </div>
    );
  }

  const stats = courierData?.stats || {};
  const couriers = courierData?.couriers || [];
  const recentInvoices = courierData?.recent_invoices || [];

  const courierChartData = couriers.filter(c => c.stats_30d.total > 0).map(c => ({
    name: c.full_name?.split(" ").pop() || c.email.split("@")[0],
    total: c.stats_30d.total,
    terminees: c.stats_30d.completed,
    en_cours: c.stats_30d.in_progress,
  }));

  const statusData = [
    { name: "Terminées", value: stats.completed_courses || 0 },
    { name: "En attente", value: stats.pending_courses || 0 },
    { name: "En cours", value: (stats.total_courses_30d || 0) - (stats.completed_courses || 0) - (stats.pending_courses || 0) },
  ].filter(d => d.value > 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0f172a" }}>
      {/* Top bar */}
      <div className="bg-slate-900/70 border-b border-white/5 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/hub" className="text-slate-400 hover:text-sky-400 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <img src={LOGO_URL} alt="CMES" className="h-8 w-auto" />
          <div>
            <span className="font-bold text-sky-400 text-sm">CMES</span>
            <span className="text-[10px] text-slate-500 block tracking-widest font-semibold">ADMIN</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-400">{user?.full_name}</span>
          <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Admin</Badge>
          <Button variant="ghost" size="icon" onClick={() => { logout(); window.location.href = "/login"; }} className="text-slate-500 hover:text-red-400">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-6 md:p-8 space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-3 bg-sky-500/20 rounded-xl">
            <Shield className="w-8 h-8 text-sky-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Tableau de bord Admin</h1>
            <p className="text-slate-400 text-sm">Supervision des agents commerciaux et suivi des expeditions</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900/60 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-500/10 rounded-lg"><Users className="w-5 h-5 text-sky-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_couriers || 0}</p>
                  <p className="text-slate-500 text-xs">Agents Commerciaux actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg"><Send className="w-5 h-5 text-emerald-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_courses_30d || 0}</p>
                  <p className="text-slate-500 text-xs">Expéditions (30j)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg"><Clock className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{stats.pending_courses || 0}</p>
                  <p className="text-slate-500 text-xs">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-emerald-400">{stats.completion_rate || 0}%</p>
                  <p className="text-slate-500 text-xs">Taux completion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-900/50 border border-white/10 p-1">
            <TabsTrigger value="couriers" className="data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400">
              Agents Commerciaux
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400">
              Factures recentes
            </TabsTrigger>
            <TabsTrigger value="charts" className="data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400">
              Graphiques
            </TabsTrigger>
          </TabsList>

          {/* Couriers Tab */}
          <TabsContent value="couriers" className="space-y-4">
            <Card className="bg-slate-900/60 border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-sky-400" /> Liste des agents commerciaux
                </CardTitle>
              </CardHeader>
              <CardContent>
                {couriers.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucun agent commercial actif</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="couriers-table">
                      <thead>
                        <tr className="text-xs text-slate-500 border-b border-white/5 uppercase tracking-wider">
                          <th className="text-left py-3 px-3">Agent Commercial</th>
                          <th className="text-left py-3 px-3">Contact</th>
                          <th className="text-center py-3 px-3">Expéditions (30j)</th>
                          <th className="text-center py-3 px-3">Terminées</th>
                          <th className="text-center py-3 px-3">En cours</th>
                          <th className="text-center py-3 px-3">Taux</th>
                        </tr>
                      </thead>
                      <tbody>
                        {couriers.map(c => {
                          const rate = c.stats_30d.total > 0 ? Math.round(c.stats_30d.completed / c.stats_30d.total * 100) : 0;
                          return (
                            <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`courier-row-${c.id}`}>
                              <td className="py-3 px-3">
                                <p className="text-white font-medium">{c.full_name}</p>
                                <p className="text-slate-500 text-xs">{c.commune}</p>
                              </td>
                              <td className="py-3 px-3">
                                <p className="text-slate-400 text-xs">{c.email}</p>
                                <p className="text-slate-500 text-xs">{c.phone}</p>
                              </td>
                              <td className="py-3 px-3 text-center text-white font-medium">{c.stats_30d.total}</td>
                              <td className="py-3 px-3 text-center text-emerald-400">{c.stats_30d.completed}</td>
                              <td className="py-3 px-3 text-center text-amber-400">{c.stats_30d.in_progress}</td>
                              <td className="py-3 px-3 text-center">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                  rate >= 80 ? "bg-emerald-500/15 text-emerald-400" :
                                  rate >= 50 ? "bg-amber-500/15 text-amber-400" :
                                  "bg-red-500/15 text-red-400"
                                }`}>{rate}%</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <Card className="bg-slate-900/60 border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-400" /> Factures recentes (30 jours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentInvoices.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucune facture recente</p>
                ) : (
                  <div className="space-y-2">
                    {recentInvoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-950/50 border border-white/5 rounded-lg" data-testid={`admin-invoice-${inv.id}`}>
                        <div>
                          <p className="text-sm text-white font-medium">{inv.invoice_number}</p>
                          <p className="text-xs text-slate-500">{inv.insurer_name}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-sky-400 font-medium">{formatCurrency(inv.amount)}</span>
                          <Badge className={`text-[10px] ${
                            inv.status === "paid" ? "bg-emerald-500/20 text-emerald-400" :
                            inv.status === "in_recovery" ? "bg-amber-500/20 text-amber-400" :
                            "bg-slate-500/20 text-slate-400"
                          }`}>{inv.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Courses by courier */}
              {courierChartData.length > 0 && (
                <Card className="bg-slate-900/60 border-white/5">
                  <CardHeader>
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-sky-400" /> Expéditions par agent commercial (30j)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={courierChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#e2e8f0" }} />
                        <Bar dataKey="terminees" fill="#10b981" name="Terminées" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="en_cours" fill="#f59e0b" name="En cours" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Status pie */}
              {statusData.length > 0 && (
                <Card className="bg-slate-900/60 border-white/5">
                  <CardHeader>
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-400" /> Répartition des expeditions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                          {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#e2e8f0" }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-4 mt-2">
                      {statusData.map((d, i) => (
                        <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-400">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                          {d.name} ({d.value})
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Quick links */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link to="/log" className="bg-slate-900/60 border border-white/5 rounded-xl p-4 text-center hover:border-sky-500/30 transition-colors group">
                <BarChart3 className="w-6 h-6 text-sky-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="text-sm text-slate-300">Dashboard LOG</p>
              </Link>
              <Link to="/log/expeditions" className="bg-slate-900/60 border border-white/5 rounded-xl p-4 text-center hover:border-sky-500/30 transition-colors group">
                <Send className="w-6 h-6 text-emerald-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="text-sm text-slate-300">Expéditions</p>
              </Link>
              <Link to="/log/invoices" className="bg-slate-900/60 border border-white/5 rounded-xl p-4 text-center hover:border-sky-500/30 transition-colors group">
                <FileText className="w-6 h-6 text-amber-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="text-sm text-slate-300">Factures</p>
              </Link>
              <Link to="/log/analytics" className="bg-slate-900/60 border border-white/5 rounded-xl p-4 text-center hover:border-sky-500/30 transition-colors group">
                <TrendingUp className="w-6 h-6 text-violet-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="text-sm text-slate-300">Analytics</p>
              </Link>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
